// Comandos do agregado Lote/OS — o que uma tela pode pedir pra fazer com uma
// OS. Um comando é dado puro (dá pra mandar por HTTP); o executor só despacha
// pras funções do domínio que já existem.
//
// Vale local (o Store roda o comando na hora) e hospedado (o servidor roda o
// mesmo executor) — mesmo código nos dois.

import type { DataISO } from "../domain/datas.ts";
import type { Snapshot } from "../domain/snapshot.ts";
import {
  cancelarLote,
  definirRecebida,
  editarLote,
  excluirLote,
  lancarLote,
  lancarLotesEmMassa,
  type DadosLote,
} from "../domain/lotes.ts";
import { assertNunca } from "../lib/assert-nunca.ts";
import { validarLinhas, type LinhaDevolucaoComando } from "./devolucao.ts";
import { gravarFicha, type FichaGravada, type IntencaoFicha } from "../domain/ficha-os.ts";

function validarFechamento(valor: unknown): { data: string; hora?: string; linhas: LinhaDevolucaoComando[] } {
  const o = comoObjeto(valor, "Fechamento");
  return {
    data: texto(o.data),
    ...(typeof o.hora === "string" && o.hora ? { hora: o.hora } : {}),
    linhas: validarLinhas(o.linhas),
  };
}
import {
  comoObjeto,
  exigirBooleano,
  exigirLista,
  exigirTexto,
  lista,
  paraNumero,
  texto,
  umDe,
} from "./_forma.ts";

/** Uma quantidade recebida por Tipo de Peça, como a tela de Lançar OS coleta. */
export interface RecebidaComando {
  tipoId: string;
  quantidade: number;
  retidaInformada?: number | null;
}

/** Uma OS da importação em massa: dados do Lote + Contagem Recebida. */
export interface EntradaLoteComando {
  dados: DadosLote;
  recebidas: RecebidaComando[];
}

export type ComandoLote =
  | { tipo: "lancar"; dados: DadosLote; recebidas: RecebidaComando[] }
  | {
      tipo: "editar";
      id: string;
      dados: DadosLote;
      recebidas: RecebidaComando[];
    }
  | ({
      /** "Salvar"/"Fechar OS" da Ficha: a Ficha inteira + a intenção (#91). */
      tipo: "gravarFicha";
      id: string;
      intencao: IntencaoFicha;
    } & FichaGravada)
  | { tipo: "lancarEmMassa"; entradas: EntradaLoteComando[] }
  | { tipo: "excluir"; id: string }
  | { tipo: "cancelar"; id: string; motivo: string };

export interface ContextoComando {
  /** "Hoje" segundo quem executa (o servidor, em produção) — nunca o cliente. */
  hoje: DataISO;
  /** Data e hora de quem executa ("AAAA-MM-DDTHH:MM:SS", mesmo relógio de
   * `hoje`) — carimba o registro do Fechamento de OS (ADR-0028). */
  agora: string;
  /** Id do usuário logado (só no servidor). Usado pra impedir auto-lockout. */
  usuarioAtual?: string;
}

export function executarComandoLote(
  snapshot: Snapshot,
  cmd: ComandoLote,
  ctx: ContextoComando,
): Snapshot {
  switch (cmd.tipo) {
    case "lancar":
      return lancarLote(snapshot, cmd.dados, cmd.recebidas ?? []);
    case "editar":
      // só a Coleta; Salvar/Fechar OS da Ficha é o `gravarFicha` (#91)
      return editarLote(snapshot, cmd.id, cmd.dados, cmd.recebidas ?? []);
    case "gravarFicha": {
      const { tipo: _tipo, id, intencao, ...ficha } = cmd;
      return gravarFicha(snapshot, id, ficha, intencao, ctx);
    }
    case "lancarEmMassa":
      // uma transição só; a tela reconstrói o resumo do diff do Snapshot
      return lancarLotesEmMassa(snapshot, cmd.entradas).snapshot;
    case "excluir":
      return excluirLote(snapshot, cmd.id);
    case "cancelar":
      return cancelarLote(snapshot, cmd.id, cmd.motivo, ctx.hoje);
    default:
      return assertNunca(cmd);
  }
}

// --- Validação da forma do comando (fronteira HTTP) -------------------------
//
// O executor acima confia que `cmd` tem a forma de `ComandoLote`. Quando o
// comando chega pela rede (server/), isso NÃO é garantido — `req.body` é
// `unknown`. `validarComandoLote` transforma um `unknown` num `ComandoLote`
// de verdade ou lança um erro em português. Só valida a FORMA; as regras de
// negócio dos campos de `dados` (cliente existe, OS não repetida…) continuam
// no domínio, que já devolve erro amigável. Helpers comuns: `./_forma.ts`.

function validarRecebidas(valor: unknown): RecebidaComando[] {
  return lista(valor, "recebidas", (item, i) => {
    const o = comoObjeto(item, `Recebida #${i + 1}`);
    return {
      tipoId: exigirTexto(o.tipoId, `recebidas[${i}].tipoId`),
      quantidade: paraNumero(o.quantidade),
      retidaInformada: o.retidaInformada == null || o.retidaInformada === "" ? null : paraNumero(o.retidaInformada),
    };
  });
}

/**
 * Antes do ADR-0013 isto era `return valor as DadosLote`, com o argumento de
 * que "o domínio valida cada campo". O domínio valida cada campo por uma
 * **regra de negócio** que por acaso também rejeita o tipo errado — e `urgente`,
 * que não tem regra de negócio, não era conferido por ninguém.
 *
 * Cada campo usa o helper que devolve a representação que o domínio já recusa
 * com mensagem amigável (`paraNumero` → NaN, `texto` → ""), então as mensagens
 * de erro não mudam. `urgente` é a exceção: sem domínio atrás para ser amigável,
 * a fronteira é estrita.
 */
function validarDados(valor: unknown): DadosLote {
  const o = comoObjeto(valor, "Dados da OS");
  return {
    clienteId: texto(o.clienteId),
    os: paraNumero(o.os),
    coletaData: texto(o.coletaData),
    coletaHora: texto(o.coletaHora),
    paradaId: o.paradaId == null ? null : texto(o.paradaId),
    sacos: o.sacos == null ? null : paraNumero(o.sacos),
    urgente: exigirBooleano(o.urgente, "urgente"),
    previstoManual: o.previstoManual == null ? null : texto(o.previstoManual),
    relavagem: o.relavagem == null ? false : exigirBooleano(o.relavagem, "relavagem"),
  };
}

export function validarComandoLote(valor: unknown): ComandoLote {
  const c = comoObjeto(valor, "Comando de OS");
  switch (c.tipo) {
    case "lancar":
      return {
        tipo: "lancar",
        dados: validarDados(c.dados),
        recebidas: validarRecebidas(c.recebidas),
      };
    case "editar":
      return {
        tipo: "editar",
        id: exigirTexto(c.id, "id"),
        dados: validarDados(c.dados),
        recebidas: validarRecebidas(c.recebidas),
      };
    case "gravarFicha": {
      const f = validarFechamento(c.fechamento);
      return {
        tipo: "gravarFicha",
        id: exigirTexto(c.id, "id"),
        intencao: umDe(c.intencao, "intencao", ["salvar", "fechar"]),
        dados: validarDados(c.dados),
        recebidas: validarRecebidas(c.recebidas),
        fechamento: { data: f.data, ...(f.hora ? { hora: f.hora } : {}), linhas: f.linhas },
        // sem regra de domínio atrás: estrita na fronteira (ADR-0013)
        reabrirFechamento: exigirBooleano(c.reabrirFechamento, "reabrirFechamento"),
      };
    }
    case "lancarEmMassa":
      return {
        tipo: "lancarEmMassa",
        entradas: exigirLista(c.entradas, "entradas", (e, i) => {
          const o = comoObjeto(e, `Entrada #${i + 1}`);
          return {
            dados: validarDados(o.dados),
            recebidas: validarRecebidas(o.recebidas),
          };
        }),
      };
    case "excluir":
      return { tipo: "excluir", id: exigirTexto(c.id, "id") };
    case "cancelar":
      return {
        tipo: "cancelar",
        id: exigirTexto(c.id, "id"),
        motivo: typeof c.motivo === "string" ? c.motivo : "",
      };
    default:
      throw new Error(`Tipo de comando de OS desconhecido: ${String(c.tipo)}.`);
  }
}
